import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Loader2, X } from 'lucide-react';
import { orgsApi, type Organization } from '../api/organizations';

interface Props {
  value: string | null;             // organization_id
  onChange: (id: string | null, org: Organization | null) => void;
  placeholder?: string;
  /** Показать кнопку «Очистить» (для фильтров). */
  clearable?: boolean;
  /** Опционально: внешний список (если уже загружен — не дёргаем API). */
  options?: Organization[];
  className?: string;
}

/**
 * Combobox с полнотекстовой фильтрацией по name/inn. Подходит и для
 * фильтра «Клиент» в Очереди, и для выбора клиента при создании тикета.
 * Закрывается по Escape и клику вне области.
 */
export function OrgCombobox({ value, onChange, placeholder = 'Поиск клиента…', clearable, options, className }: Props) {
  const [orgs, setOrgs] = useState<Organization[]>(options || []);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [highlighted, setHighlighted] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Загрузка списка (один раз). Список «не очень большой» — десятки.
  useEffect(() => {
    if (options) { setOrgs(options); return; }
    setLoading(true);
    orgsApi.list().then(setOrgs).catch(() => {}).finally(() => setLoading(false));
  }, [options]);

  // Закрытие по клику вне.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const selected = orgs.find(o => o.id === value) || null;

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return orgs;
    return orgs.filter(o =>
      o.name.toLowerCase().includes(t) ||
      o.inn.includes(t) ||
      (o.contact_person || '').toLowerCase().includes(t)
    );
  }, [orgs, q]);

  useEffect(() => { setHighlighted(0); }, [q, open]);

  const pick = (o: Organization) => {
    onChange(o.id, o);
    setOpen(false);
    setQ('');
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') setOpen(true);
      return;
    }
    if (e.key === 'Escape') { setOpen(false); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlighted(h => Math.min(h + 1, filtered.length - 1)); }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setHighlighted(h => Math.max(h - 1, 0)); }
    if (e.key === 'Enter')     { e.preventDefault(); if (filtered[highlighted]) pick(filtered[highlighted]); }
  };

  return (
    <div ref={wrapRef} className={`relative ${className || ''}`}>
      <div className="flex items-center gap-1 bg-slate-950 border border-slate-700 rounded px-2 py-1 focus-within:border-blue-500 transition">
        <input value={open ? q : (selected ? `${selected.name}` : '')}
          placeholder={selected ? '' : placeholder}
          onFocus={() => setOpen(true)}
          onChange={e => { setQ(e.target.value); setOpen(true); }}
          onKeyDown={onKey}
          className="flex-1 bg-transparent text-xs text-white outline-none min-w-0" />
        {selected && !open && (
          <span className="text-[10px] font-mono text-slate-500 shrink-0">{selected.inn}</span>
        )}
        {loading && <Loader2 size={12} className="animate-spin text-slate-500 shrink-0" />}
        {clearable && value && !loading && (
          <button type="button" onClick={() => onChange(null, null)} className="text-slate-500 hover:text-red-400 shrink-0">
            <X size={12} />
          </button>
        )}
        <ChevronDown size={12} className="text-slate-500 shrink-0" />
      </div>

      {open && (
        <div className="absolute z-50 mt-1 w-full max-w-md bg-slate-900 border border-slate-700 rounded shadow-2xl max-h-72 overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-xs text-slate-500">Ничего не найдено</div>
          ) : filtered.map((o, idx) => (
            <button key={o.id} type="button" onMouseEnter={() => setHighlighted(idx)} onClick={() => pick(o)}
              className={`w-full text-left px-3 py-2 text-xs hover:bg-slate-800 ${
                idx === highlighted ? 'bg-slate-800' : ''
              } ${value === o.id ? 'border-l-2 border-blue-500' : ''}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="text-white truncate">
                  {o.contract_active === false && <span className="text-red-400 mr-1">●</span>}
                  {o.name}
                </span>
                <span className="font-mono text-slate-500 text-[10px] shrink-0">{o.inn}</span>
              </div>
              {o.contact_person && (
                <div className="text-[10px] text-slate-500 truncate">{o.contact_person}</div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
